/**
 * NxCorreccionMarcaje — corrige un marcaje de asistencia SIN pisar la marca original.
 *
 * ASIST-FIX-VISTA (PANEL-ASISTENCIA-VALIDATION-V2-001): la contraparte visual del
 * endpoint auditado de Argy-A (bit #4249, PR nexo-api#147):
 *   PATCH /api/v1/businesses/{businessId}/asistencia/{id}/correccion
 *   { timestampCorregido, motivo (>=5 chars OBLIGATORIO), corregidoPor? }
 *
 * Dos piezas:
 *  - abrirCorreccionDia({businessId, persona, fecha, onDone}): modal que lista los
 *    marcajes REALES de esa persona ese dia (con su id), deja elegir cual corregir,
 *    pide hora nueva + motivo y llama el endpoint auditado. La marca original nunca
 *    se toca: la correccion es una capa (attendance_correcciones).
 *  - bindDiaAcciones(container, opts): puebla las zonas .nx-dia-accion que
 *    NxResumenPorPersona deja reservadas (UIA-T12) con el boton de corregir.
 *    Solo actua si hay negocio seleccionado y el rol puede escribir asistencia.
 *
 * Fail-loud deliberado: si la persona no aparece en los marcajes del dia (nombre
 * que no matchea, negocio equivocado en el selector), el modal LO DICE en vez de
 * dejar corregir a ciegas.
 *
 * @module presentation/shared/NxCorreccionMarcaje v1.0
 */

import { AsistenciaApiClient } from '../../infrastructure/api/AsistenciaApiClient.js';
import { getSelectedBusiness, getSelectedBusinessName } from '../components/BusinessSelector.js';
import { AppConfig } from '../../config.js';

// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc } from './texto.js';

const TIPO_LABEL = {
  entrada: '→ Entrada', salida: '← Salida',
  almuerzo_inicio: '🍽 Inicio almuerzo', almuerzo_fin: '🍽 Fin almuerzo',
  descanso_inicio: 'Inicio descanso', descanso_fin: 'Fin descanso'
};

function puedeCorregir() {
  return AppConfig.role === 'dev' || AppConfig.role === 'admin' || AppConfig.role === 'manager';
}

function fmtHora(iso) {
  try { return new Date(iso).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', second: '2-digit' }); }
  catch { return String(iso); }
}

function toLocalInputValue(iso) {
  try {
    const d = new Date(iso);
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  } catch { return ''; }
}

/**
 * Normaliza la fecha que viene en la fila del resumen (puede llegar como
 * '2026-08-05', '05/08/2026' o con hora) a YYYY-MM-DD. Devuelve null si no se puede.
 */
function normalizarFecha(fechaTexto) {
  const s = String(fechaTexto || '').trim();
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  const d = new Date(s);
  return isNaN(d) ? null : d.toISOString().slice(0, 10);
}

export async function abrirCorreccionDia({ businessId, persona, fecha, onDone }) {
  document.getElementById('nx-corr-modal')?.remove();

  const fechaISO = normalizarFecha(fecha);
  const overlay = document.createElement('div');
  overlay.id = 'nx-corr-modal';
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal-box" style="max-width:480px">
      <div class="modal-header">
        <span class="modal-title">&#9998; Corregir marcaje — ${esc(persona)}</span>
        <button class="modal-close" id="nx-corr-close">&#x2715;</button>
      </div>
      <div class="modal-body">
        <p style="font-size:0.875rem;color:#8b949e;margin-bottom:10px">
          ${esc(fecha)} · negocio <strong>${esc(getSelectedBusinessName() || businessId)}</strong>.
          La marca original <strong>no se pierde</strong>: la correccion queda como capa auditada
          (quien, cuando, motivo, valor anterior).
        </p>
        <div id="nx-corr-body"><p style="font-size:0.875rem;color:#8b949e">Buscando los marcajes de ese dia...</p></div>
      </div>
    </div>`;
  document.body.appendChild(overlay);
  const close = () => overlay.remove();
  document.getElementById('nx-corr-close').onclick = close;
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

  const $body = document.getElementById('nx-corr-body');

  if (!fechaISO) {
    $body.innerHTML = `<p style="font-size:0.875rem;color:#f85149">No pude interpretar la fecha "${esc(fecha)}" para buscar los marcajes.</p>`;
    return;
  }

  let marcajes = [];
  try {
    const res = await AsistenciaApiClient.list(businessId, {
      fechaDesde: fechaISO, fechaHasta: fechaISO, pageSize: 100
    });
    if (!res.ok) throw new Error(res.error || 'error consultando marcajes');
    const items = res.data?.data?.items || res.data?.data || [];
    const objetivo = String(persona).trim().toLowerCase();
    marcajes = items.filter(a => String(a.nombreColaborador || '').trim().toLowerCase() === objetivo);
  } catch (err) {
    $body.innerHTML = `<p style="font-size:0.875rem;color:#f85149">Error consultando marcajes: ${esc(err.message)}</p>`;
    return;
  }

  if (marcajes.length === 0) {
    $body.innerHTML = `
      <p style="font-size:0.875rem;color:#d29922">
        Ningun marcaje de <strong>${esc(persona)}</strong> el ${esc(fechaISO)} en
        <strong>${esc(getSelectedBusinessName() || businessId)}</strong>.
      </p>
      <p style="font-size:0.875rem;color:#8b949e">
        Revisa que el negocio del selector (arriba) sea el correcto; el resumen que estas
        viendo puede ser de otro negocio. No se corrige a ciegas.
      </p>`;
    return;
  }

  marcajes.sort((a, b) => String(a.timestampEvento).localeCompare(String(b.timestampEvento)));

  $body.innerHTML = `
    <label class="form-label">Cual marca corregir</label>
    <select id="nx-corr-marca" class="form-control" style="font-size:14px">
      ${marcajes.map(m => `<option value="${esc(m.id)}" data-ts="${esc(m.timestampEvento)}">
        ${esc(TIPO_LABEL[m.tipo] || m.tipo)} · ${esc(fmtHora(m.timestampEvento))}${m.metodo && m.metodo !== 'manual' ? ` (${esc(m.metodo)})` : ''}
      </option>`).join('')}
    </select>
    <label class="form-label" style="margin-top:0.75rem">Hora corregida</label>
    <input type="datetime-local" id="nx-corr-ts" class="form-control" style="font-size:14px">
    <label class="form-label" style="margin-top:0.75rem">Motivo (obligatorio, minimo 5 caracteres)</label>
    <input type="text" id="nx-corr-motivo" class="form-control" style="font-size:14px"
      placeholder="Ej: marco el fin de almuerzo al salir, se corrige a la 1:30pm">
    <p id="nx-corr-error" class="auth-error" style="display:none;margin-top:0.5rem"></p>
    <div class="modal-footer" style="margin-top:1rem;padding:0">
      <button class="btn btn-ghost" id="nx-corr-cancel">Cancelar</button>
      <button class="btn btn-primary" id="nx-corr-save">Guardar correccion</button>
    </div>`;

  const $marca = document.getElementById('nx-corr-marca');
  const $ts = document.getElementById('nx-corr-ts');
  const syncTs = () => {
    const opt = $marca.selectedOptions[0];
    if (opt) $ts.value = toLocalInputValue(opt.dataset.ts);
  };
  syncTs();
  $marca.addEventListener('change', syncTs);
  document.getElementById('nx-corr-cancel').onclick = close;

  document.getElementById('nx-corr-save').onclick = async () => {
    const id = $marca.value;
    const ts = $ts.value;
    const motivo = document.getElementById('nx-corr-motivo').value.trim();
    const $err = document.getElementById('nx-corr-error');
    const $btn = document.getElementById('nx-corr-save');
    $err.style.display = 'none';

    if (!ts) { $err.textContent = 'La hora corregida es requerida'; $err.style.display = ''; return; }
    if (motivo.length < 5) { $err.textContent = 'El motivo es obligatorio (minimo 5 caracteres): sin motivo la correccion no sirve para responder una discusion'; $err.style.display = ''; return; }

    $btn.disabled = true; $btn.textContent = 'Guardando...';
    try {
      const res = await AsistenciaApiClient.correccion(businessId, id, {
        timestampCorregido: new Date(ts).toISOString(),
        // #17507: sin corregidoPor. Quien corrige lo pone la API desde el token; si lo manda el
        // navegador, el rastro de attendance_correcciones dice lo que el navegador quiera.
        motivo
      });
      if (!res.ok) throw new Error(res.error || 'la API rechazo la correccion');
      const d = res.data?.data || res.data || {};
      $body.innerHTML = `
        <div style="border-left:3px solid #3fb950;background:#0d2818;border-radius:6px;padding:10px 12px">
          <p style="font-size:0.875rem;color:#3fb950;font-weight:600;margin:0 0 6px 0">Correccion guardada con auditoria</p>
          <p style="font-size:0.875rem;color:#c9d1d9;margin:0">
            Marcado: ${esc(fmtHora(d.timestampMarcado || ''))} → Corregido: <strong>${esc(fmtHora(d.timestampCorregido || ts))}</strong>
            ${d.minutosDeAjuste != null ? ` · ajuste ${d.minutosDeAjuste > 0 ? '+' : ''}${esc(d.minutosDeAjuste)} min` : ''}
            ${d.vecesCorregido != null ? ` · ${esc(d.vecesCorregido)}ª correccion de esta marca` : ''}
          </p>
          <p style="font-size:0.875rem;color:#8b949e;margin:6px 0 0 0">La marca original sigue intacta en el registro.</p>
        </div>
        <div class="modal-footer" style="margin-top:1rem;padding:0">
          <button class="btn btn-primary" id="nx-corr-ok">Listo</button>
        </div>`;
      document.getElementById('nx-corr-ok').onclick = () => { close(); if (typeof onDone === 'function') onDone(d); };
    } catch (err) {
      $btn.disabled = false; $btn.textContent = 'Guardar correccion';
      $err.textContent = 'Error: ' + (err.message || 'intenta de nuevo');
      $err.style.display = '';
    }
  };
}

/**
 * Puebla las zonas .nx-dia-accion (reservadas por NxResumenPorPersona / UIA-T12)
 * dentro de `container` con el boton de corregir. Delegacion: un solo listener.
 * No-op silencioso si no hay negocio seleccionado o el rol no puede escribir:
 * el resumen sigue siendo el mismo de siempre, solo que sin boton.
 */
export function bindDiaAcciones(container, { onDone } = {}) {
  if (!container || !puedeCorregir()) return 0;
  const businessId = getSelectedBusiness();
  if (!businessId) return 0;

  const zonas = container.querySelectorAll('.nx-dia-accion:not([data-corr-bound])');
  zonas.forEach(z => {
    z.setAttribute('data-corr-bound', '1');
    // La persona es el titulo del <details> padre (title del div de nombre).
    const persona = z.closest('details')?.querySelector('[title]')?.getAttribute('title') || '';
    if (!persona || !z.dataset.fecha) return;
    z.innerHTML = `<button type="button" class="nx-corr-btn" data-fecha="${esc(z.dataset.fecha)}" data-persona="${esc(persona)}"
      title="Corregir un marcaje de este dia (queda auditado)"
      style="background:none;border:1px solid #30363d;border-radius:4px;color:#8b949e;cursor:pointer;font-size:0.875rem;padding:1px 7px">&#9998;</button>`;
  });

  if (zonas.length > 0 && !container.hasAttribute('data-corr-delegado')) {
    container.setAttribute('data-corr-delegado', '1');
    container.addEventListener('click', (e) => {
      const btn = e.target.closest('.nx-corr-btn');
      if (!btn) return;
      e.preventDefault();
      const zona = btn.closest('.nx-dia-accion');
      abrirCorreccionDia({
        businessId: getSelectedBusiness(),
        persona: btn.dataset.persona,
        fecha: btn.dataset.fecha,
        // REVIEW #4423: sin feedback, el renglon del chat seguia mostrando la hora
        // vieja como si nada hubiera pasado. El chip dice la verdad: la correccion
        // entro, y lo que esta a la izquierda es el dato de ANTES (esta burbuja es
        // una respuesta historica; se recalcula al volver a consultar).
        onDone: (detalle) => {
          if (zona) {
            zona.innerHTML = `<span title="Correccion guardada con auditoria. Este renglon muestra el dato de antes de la correccion; vuelve a consultar para ver el efecto."
              style="font-size:0.875rem;color:#3fb950;border:1px solid #3fb950;border-radius:8px;padding:1px 6px;white-space:nowrap">✓ corregido${detalle?.timestampCorregido ? ' → ' + esc(fmtHora(detalle.timestampCorregido)) : ''}</span>`;
          }
          if (typeof onDone === 'function') onDone(detalle);
        }
      });
    });
  }
  return zonas.length;
}
